/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */

import genericStyles from "../../Styles/genericStyles.module.css";
import MUIDataTableCustomStyle from "../../Components/MUIDataTableCustomStyle";
import { Vocabulary } from "../../Utils/Vocabulary";
import { useState, createContext } from "react";
import { endpoints } from "../../Utils/UrlEnum";
import { AutocompleteChangeReason, Button, Chip } from "@mui/material";
import { InstructorModel } from "../../Utils/Models";
import { ETableEditAction, EWidths } from "../../Utils/Enums";
import GenericModal from "../GenericModal";
import { FormsIds, TablesIds } from "../../Utils/Constants";

import InstructorsForm from "./InstructorsForm";
import UserInfoView from "./UserInfoView";
import withRole from "../../Hooks/withRole";
import { hexToColorName } from "../../Utils/Utils";
import { Role } from "../../Utils/Types";

export const CRUDContext = createContext(new InstructorModel());

/**
 * Returns true if the given row data represents an instructor
 * (i.e. has at least one role named "Instructor").
 */
const checkIsInstructor = (rowData: any): boolean => {
  return rowData?.roleIds?.some((role: any) => role.name === "Instructor");
};

function InstructorsTable(props: any) {
  const [contextValue, setContextValue] = useState(new InstructorModel());
  const [open, setOpen] = useState(false);
  const [triggerReload, setTriggerReload] = useState<undefined | boolean>();
  const [isInstructorSelected, setIsInstructorSelected] = useState(true);

  const showAllEmployees: boolean = props?.showAllEmployees === true;

  /**
   * Base columns shown in both modes
   */
  const instructorColumns = [
    {
      label: `${Vocabulary.firstName}`,
      name: "firstName",
      options: {
        sort: false,
      },
    },
    {
      label: `${Vocabulary.lastName}`,
      name: "lastName",
      options: {
        sort: false,
      },
    },
    {
      label: `${Vocabulary.username}`,
      name: "username",
      options: {
        sort: false,
      },
    },
    {
      label: `${Vocabulary.email}`,
      name: "email",
      options: {
        sort: false,
      },
    },
  ];

  /**
   * Extra column shown only in showAllEmployees mode
   */
  const rolesColumn = {
    label: `${Vocabulary.roles}`,
    name: "roleIds",
    options: {
      setCellHeaderProps: () => ({
        align: "center",
      }),
      setCellProps: () => ({
        align: "center",
      }),
      filter: false,
      sort: false,
      empty: true,
      customBodyRender: (rowData: Role[]) => {
        return (
          <>
            {(rowData || []).map((role: Role) => (
              <Chip
                sx={{ backgroundColor: "white !important" }}
                key={role._id}
                label={role.name}
                style={{
                  padding: 5,
                }}
              />
            ))}
          </>
        );
      },
      exportValue: (rowData: Role[]) =>
        rowData?.map((role: Role) => role.name).join(", ") || "",
    },
  };

  const columns = showAllEmployees
    ? [...instructorColumns, rolesColumn]
    : instructorColumns;

  const instructorExpandableColumns = [
    {
      label: `${Vocabulary.brand}`,
      name: "brand",
      options: {
        sort: false,
      },
    },
    {
      label: `${Vocabulary.model}`,
      name: "model",
      options: {
        sort: false,
      },
    },
    {
      label: `${Vocabulary.vehicleNumber}`,
      name: "vehicleNumber",
      options: {
        sort: false,
      },
    },
    {
      label: `${Vocabulary.color}`,
      name: "color",
      options: {
        setCellHeaderProps: () => ({
          align: "center",
        }),
        setCellProps: () => ({
          align: "center",
        }),
        filter: false,
        sort: false,

        empty: true,
        customBodyRender: (rowData: string) => {
          return (
            <>
              <Chip
                key={rowData?.toString()}
                label={""}
                style={{
                  margin: 5,
                  background: rowData,
                }}
              />
            </>
          );
        },
        exportValue: (rowData: string) => hexToColorName(rowData),
      },
    },
  ];

  /**
   * Wraps the base setContextValue to also determine whether the selected
   * row represents an instructor, updating isInstructorSelected accordingly.
   */
  const handleSetContextValue = (value: any) => {
    setContextValue(value);
    if (showAllEmployees) {
      setIsInstructorSelected(checkIsInstructor(value));
    }
  };

  const handleClose = () => {
    setOpen(false);
    setContextValue(new InstructorModel());
    if (showAllEmployees) {
      setIsInstructorSelected(true);
    }
  };

  /**
   * Modal actions for instructor: cancel + save
   */
  const instructorModalActions = (
    <div className={genericStyles.modalActions}>
      <Button
        color="secondary"
        onClick={handleClose}
        variant="outlined"
        fullWidth
      >
        {Vocabulary.cancel}
      </Button>
      <Button
        fullWidth
        type="submit"
        form={FormsIds.instructorForm}
        variant="contained"
      >
        {Vocabulary.save}
      </Button>
    </div>
  );

  /**
   * Modal actions for non-instructor: cancel + save documents
   */
  const readOnlyModalActions = (
    <div className={genericStyles.modalActions}>
      <Button
        color="secondary"
        onClick={handleClose}
        variant="outlined"
        fullWidth
      >
        {Vocabulary.cancel}
      </Button>
      <Button
        fullWidth
        type="submit"
        form={FormsIds.employeeDocumentsForm}
        variant="contained"
      >
        {Vocabulary.save}
      </Button>
    </div>
  );

  return (
    <div className={genericStyles.mainContainer}>
      <CRUDContext.Provider value={contextValue}>
        <MUIDataTableCustomStyle
          onEditAction={ETableEditAction.MODAL}
          title={showAllEmployees ? Vocabulary.angajati : Vocabulary.instructors}
          columns={columns}
          CRUDEndPoint={
            showAllEmployees
              ? endpoints.instructor.byWorkPoint
              : endpoints.instructor.user
          }
          id={TablesIds.instructor}
          handleModalForm={setOpen}
          hideAddButton={true}
          {...(!showAllEmployees && {
            expandableRows: true,
            expandableRowsHeader: true,
            expandableRowObjectLocationName:
              "instructorDetails.0.vehicleDetails",
            expandableColumns: instructorExpandableColumns,
          })}
          addOrEditForm={
            <GenericModal
              onClose={handleClose}
              maxWidth={EWidths.MD}
              open={open}
              actions={
                showAllEmployees && !isInstructorSelected
                  ? readOnlyModalActions
                  : instructorModalActions
              }
              title={Vocabulary.editModalTitle}
            >
              {showAllEmployees && !isInstructorSelected ? (
                <UserInfoView
                  onClose={() => {
                    setOpen(false);
                    setTriggerReload(
                      triggerReload !== undefined ? !triggerReload : false
                    );
                    setContextValue(new InstructorModel());
                    setIsInstructorSelected(true);
                  }}
                  setContextValue={setContextValue}
                />
              ) : (
                <InstructorsForm
                  setContextValue={(
                    e: any,
                    client: InstructorModel | null,
                    reason: AutocompleteChangeReason,
                    details?: any
                  ) => ({})}
                  onClose={() => {
                    setOpen(false);
                    setTriggerReload(
                      triggerReload !== undefined ? !triggerReload : false
                    );
                    setContextValue(new InstructorModel());
                  }}
                />
              )}
            </GenericModal>
          }
          setContextValue={handleSetContextValue}
          triggerReload={triggerReload}
          hideDeleteButton={!props?.delete}
          showPrintButton
          showDownloadButton
          exportWithTimestamp
        />
      </CRUDContext.Provider>
    </div>
  );
}

export default withRole(InstructorsTable);
